import React from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import type { SelectChangeEvent } from '@mui/material/Select'
import type { PrepDateBasis } from '../../api/prep'
import {
  PREP_AGE_MAX_DAY_OPTIONS,
} from '../../store/userSettings'
import type { PrepAgeMaxDays } from '../../api/userSettings'

export const PREP_DATE_BASIS_TOOLTIP =
  'Choose whether items are ordered and filtered by the date they were added to Prep or the date they were originally published.'

const DATE_BASIS_OPTIONS: Array<{
  value: PrepDateBasis
  label: string
  description: string
}> = [
  {
    value: 'created_at',
    label: 'Added to Prep',
    description:
      'Show the most recently added Prep items first. The age filter uses the date each item was added.',
  },
  {
    value: 'published_at',
    label: 'Originally published',
    description:
      'Show the most recently published stories first. The age filter uses the original source publication date.',
  },
]

interface PrepDateFiltersProps {
  dateBasis: PrepDateBasis
  onDateBasisChange: (basis: PrepDateBasis) => void
  ageMaxDays: PrepAgeMaxDays
  onAgeMaxDaysChange: (days: PrepAgeMaxDays) => void
}

const PrepDateFilters: React.FC<PrepDateFiltersProps> = ({
  dateBasis,
  onDateBasisChange,
  ageMaxDays,
  onAgeMaxDaysChange,
}) => {
  const handleDateBasisChange = (event: SelectChangeEvent<PrepDateBasis>) => {
    const value = event.target.value
    if (value === 'created_at' || value === 'published_at') {
      onDateBasisChange(value)
    }
  }

  return (
    <Box
      data-testid="prep-date-filters"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}
    >
      <Tooltip
        title={PREP_DATE_BASIS_TOOLTIP}
        placement="top"
        enterDelay={300}
        describeChild
        disableInteractive
      >
        <FormControl size="small" sx={{ minWidth: { xs: 152, sm: 178 } }}>
          <InputLabel id="prep-date-basis-label">Date basis</InputLabel>
          <Select<PrepDateBasis>
            labelId="prep-date-basis-label"
            id="prep-date-basis"
            value={dateBasis}
            label="Date basis"
            onChange={handleDateBasisChange}
          >
            {DATE_BASIS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Tooltip title={option.description} placement="right" enterDelay={300} describeChild>
                  <Box component="span" sx={{ display: 'block', width: '100%' }}>
                    {option.label}
                  </Box>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Tooltip>

      <Tooltip title="Limit items by age using the selected date basis" enterDelay={300} describeChild>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={ageMaxDays}
          onChange={(_event, next) => {
            if (next !== null) {
              onAgeMaxDaysChange(next as PrepAgeMaxDays)
            }
          }}
          aria-label="Filter items by age"
          sx={{
            '& .MuiToggleButton-root': {
              px: 0.75,
              fontWeight: 500,
            },
          }}
        >
          {PREP_AGE_MAX_DAY_OPTIONS.map((option) => (
            <ToggleButton
              key={option}
              value={option}
              aria-label={option === 0 ? 'Show all ages' : `Show last ${option} day${option === 1 ? '' : 's'}`}
            >
              {option === 0 ? 'All' : `${option}d`}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Tooltip>
    </Box>
  )
}

export default PrepDateFilters
